import { Component, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { WebAuthnService } from '../../services/webauthn.service';
import { PasskeyService } from '../../services/passkey.service';
import { PinAuthContext, SecurityService } from '../../services/security.service';
import { LoggerService } from '../../services/logger.service';
import { ToastService } from '../../services/notification/toast.service';

/**
 * Modal de Onboarding para Passkey.
 * 
 * Se muestra después de que el usuario valida su PIN exitosamente
 * y NO tiene una passkey registrada.
 * 
 * Propósito:
 * - Educar al usuario sobre los beneficios de Passkey
 * - Ofrecer activar Passkey de forma opcional
 * - Permitir rechazar y continuar con PIN tradicional
 * 
 * Flujo:
 * 1. Usuario valida PIN correctamente
 * 2. Sistema detecta que NO tiene passkey
 * 3. Muestra este modal
 * 4. Usuario elige:
 *    A) Activar Passkey → Registra y contin��a
 *    B) Recordarme después → Guarda preferencia y continúa
 * 
 * @example
 * <app-passkey-onboarding-modal
 *   (close)="onModalClose($event)">
 * </app-passkey-onboarding-modal>
 */
@Component({
  selector: 'app-passkey-onboarding-modal',
  standalone: true,
  imports: [MatIconModule],
  templateUrl: './passkey-onboarding-modal.component.html',
  styleUrls: ['./passkey-onboarding-modal.component.scss']
})
export class PasskeyOnboardingModalComponent implements OnInit, OnDestroy {

  /**
   * Evento que se emite cuando el modal se cierra.
   * Retorna true si se registró la passkey, false si se rechazó.
   */
  @Output() close = new EventEmitter<boolean>();

  /**
   * Indica si se está procesando el registro de passkey.
   */
  isRegistering: boolean = false;

  /**
   * Mensaje de error si falla el registro.
   */
  registrationError: string | null = null;

  /**
   * Nombre del método biométrico disponible.
   * Ejemplos: "Face ID", "Touch ID", "Windows Hello", "Huella Digital"
   */
  biometricMethodName: string = 'Passkey';
  private authContextSubscription: Subscription | null = null;
  private registrationAbortController: AbortController | null = null;
  private registrationGeneration = 0;

  constructor(
    private webAuthnService: WebAuthnService,
    private passkeyService: PasskeyService,
    private securityService: SecurityService,
    private logger: LoggerService,
    private toast: ToastService
  ) {}

  async ngOnInit(): Promise<void> {
    const authContextChanges$ = (this.securityService as any).authContextChanges$;
    if (authContextChanges$) {
      this.authContextSubscription = authContextChanges$.subscribe(() => this.cancelRegistration());
    }
    this.logger.log('🔐 [PASSKEY-ONBOARDING] Modal de onboarding inicializado');
    
    // Obtener nombre del método biométrico
    try {
      this.biometricMethodName = await this.webAuthnService.getBiometricMethodName();
      this.logger.log('✅ [PASSKEY-ONBOARDING] Método biométrico:', this.biometricMethodName);
    } catch (error) {
      this.logger.error('❌ [PASSKEY-ONBOARDING] Error obteniendo método biométrico:', error);
      this.biometricMethodName = 'Passkey';
    }
  }

  ngOnDestroy(): void {
    this.authContextSubscription?.unsubscribe();
    this.cancelRegistration();
  }

  /**
   * Activa la passkey para el usuario.
   * 
   * Flujo:
   * 1. Obtiene email del token JWT (usuario ya está autenticado)
   * 2. Partner siempre es null en este portal
   * 3. Llama a PasskeyService.registerPasskey()
   * 4. Si exitoso → Emite evento close(true)
   * 5. Si falla → Muestra error
   */
  async activatePasskey(): Promise<void> {
    if (this.isRegistering) return;
    this.logger.log('🔐 [PASSKEY-ONBOARDING] ========================================');
    this.logger.log('🔐 [PASSKEY-ONBOARDING] Usuario eligió activar passkey');
    const context = this.securityService.capturePinAuthContext();
    const controller = new AbortController();
    const generation = ++this.registrationGeneration;
    this.registrationAbortController = controller;
    this.isRegistering = true;
    this.registrationError = null;

    try {
      // Obtener email del token JWT
      const email = context.email;
      
      if (!email) {
        throw new Error('No se pudo obtener el email del usuario');
      }

      const partner = context.partner;

      this.logger.log('👤 [PASSKEY-ONBOARDING] Email:', email);
      this.logger.log('🏢 [PASSKEY-ONBOARDING] Partner:', partner);
      this.logger.log('📱 [PASSKEY-ONBOARDING] Dispositivo:', navigator.userAgent);

      // Obtener nombre del dispositivo
      const deviceName = await this.getDeviceName();
      if (!this.isCurrentRegistration(generation, context)) return;
      this.logger.log('📱 [PASSKEY-ONBOARDING] Nombre del dispositivo:', deviceName);

      // Registrar passkey
      this.logger.log('📡 [PASSKEY-ONBOARDING] Llamando a PasskeyService.registerPasskey()...');
      const response = await this.passkeyService.registerPasskey(email, partner as any, deviceName, { signal: controller.signal });
      if (!this.isCurrentRegistration(generation, context)) return;

      this.logger.log('✅ [PASSKEY-ONBOARDING] Passkey registrada exitosamente');
      this.logger.log('   → Credential ID:', response.credentialId);
      this.logger.log('   → Device Name:', response.deviceName);

      // Mostrar mensaje de éxito
      this.toast.showSuccess(`Passkey activada. La próxima vez podrás validar con ${this.biometricMethodName}`);

      // Cerrar modal indicando que se registró exitosamente
      this.close.emit(true);

    } catch (error: any) {
      if (!this.isCurrentRegistration(generation, context)) return;
      this.logger.error('❌ [PASSKEY-ONBOARDING] Error registrando passkey:', error);
      
      // Mostrar error al usuario
      const errorMessage = error.message || 'Error al activar Passkey. Intenta nuevamente.';
      this.registrationError = errorMessage;
      this.isRegistering = false;

      // También mostrar toast
      this.toast.showError(errorMessage);
    } finally {
      if (this.isCurrentRegistration(generation, context)) {
        this.isRegistering = false;
        this.registrationAbortController = null;
      }
    }
  }

  private cancelRegistration(): void {
    this.registrationGeneration++;
    this.registrationAbortController?.abort();
    this.registrationAbortController = null;
    this.isRegistering = false;
  }

  private isCurrentRegistration(generation: number, context: PinAuthContext): boolean {
    const current = this.securityService.capturePinAuthContext();
    return generation === this.registrationGeneration &&
      !this.registrationAbortController?.signal.aborted &&
      context.email === current.email &&
      context.partner === current.partner &&
      context.sessionId === current.sessionId &&
      context.authRevision === current.authRevision;
  }

  /**
   * Usuario rechaza activar passkey.
   * 
   * Guarda preferencia en localStorage para no mostrar
   * el modal nuevamente en futuros logins.
   */
  remindMeLater(): void {
    this.logger.log('ℹ️ [PASSKEY-ONBOARDING] Usuario eligió "Recordarme después"');
    
    // Guardar preferencia en localStorage
    localStorage.setItem('passkey_onboarding_declined', 'true');
    localStorage.setItem('passkey_onboarding_declined_at', new Date().toISOString());
    
    this.logger.log('💾 [PASSKEY-ONBOARDING] Preferencia guardada en localStorage');
    
    // Cerrar modal indicando que NO se registró
    this.close.emit(false);
  }

  /**
   * Obtiene un nombre descriptivo del dispositivo actual.
   * 
   * Basado en el User Agent, genera un nombre como:
   * - "iPhone de Safari"
   * - "MacBook de Chrome"
   * - "Windows de Edge"
   * - "Android de Chrome"
   * 
   * @returns Nombre descriptivo del dispositivo
   */
  private async getDeviceName(): Promise<string> {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detectar dispositivo
    let device = 'Dispositivo';
    if (/iphone/.test(userAgent)) {
      device = 'iPhone';
    } else if (/ipad/.test(userAgent)) {
      device = 'iPad';
    } else if (/macintosh|mac os x/.test(userAgent)) {
      device = 'MacBook';
    } else if (/android/.test(userAgent)) {
      device = 'Android';
    } else if (/windows/.test(userAgent)) {
      device = 'Windows';
    }
    
    // Detectar navegador
    let browser = '';
    if (/chrome/.test(userAgent) && !/edg/.test(userAgent)) {
      browser = 'Chrome';
    } else if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
      browser = 'Safari';
    } else if (/firefox/.test(userAgent)) {
      browser = 'Firefox';
    } else if (/edg/.test(userAgent)) {
      browser = 'Edge';
    }
    
    return browser ? `${device} de ${browser}` : device;
  }
}
